import { formatNumber } from '@agro1desenvolvimento/utils-js';
import dayjs from 'dayjs';
import React from 'react';
import {
  AuditoriaAplicacaoObjeto,
  TypedAplication,
  Usuario,
} from '../../@types';
import { PendingAproveMainObject } from './pending-aprove-main-object.builder';

const LOCALE = 'pt-BR';
const DOSE_FORMAT = { minimumFractionDigits: 4, maximumFractionDigits: 4 };
const AREA_FORMAT = { minimumFractionDigits: 2, maximumFractionDigits: 2 };

function formatTooltipDate(date: string): string {
  const parsed = dayjs(date);
  return parsed.isValid() ? parsed.format('DD/MM/YYYY') : date;
}

function getResponsaveisAprovacao(
  propriedadeId: number,
  usuariosPorPropriedade: Record<number, Usuario[]>,
): string {
  const usuarios = usuariosPorPropriedade[propriedadeId] || [];
  if (usuarios.length === 0) return '';
  return usuarios.map((usuario) => usuario.nome).join(', ');
}

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && value !== '';
}

const Line = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <div>
    {label} <b>{children}</b>
  </div>
);

function buildProdutosConcat(
  item: TypedAplication,
  parsedObjeto: AuditoriaAplicacaoObjeto | null,
): React.ReactNode {
  if (item.type === 'recomendacoes') {
    return item.insumos.map((produto) => (
      <div key={produto.descricao}>
        <b>
          {produto.descricao.trim()} - Dose:{' '}
          {formatNumber(produto.dose, DOSE_FORMAT, LOCALE)}{' '}
          {produto.unidade.abreviatura.trim()}/ha
        </b>
      </div>
    ));
  }

  if (item.type === 'aqilaRecomendacoes') {
    return item.produtos.map((produto) => (
      <div key={produto.produto.descricao}>
        <b>
          {produto.produto.descricao.trim()} - Dose:{' '}
          {formatNumber(Number(produto.qtDose), DOSE_FORMAT, LOCALE)}{' '}
          {produto.unidade.abreviatura.trim()}/ha
        </b>
      </div>
    ));
  }

  const produtosAuditoria = parsedObjeto?.produtos || [];
  return produtosAuditoria.map((produto) => (
    <div key={produto.descricao}>
      <b>
        {produto.descricao.trim()} - Dose:{' '}
        {formatNumber(produto.dose, DOSE_FORMAT, LOCALE)}{' '}
        {produto.unidade.abreviatura.trim()}/ha - Total:{' '}
        {formatNumber(produto.quantidadeTotal, DOSE_FORMAT, LOCALE)}
      </b>
    </div>
  ));
}

function buildRecomendacaoTooltip(
  mainObject: PendingAproveMainObject,
  produtos: React.ReactNode,
  responsaveis: string,
): React.ReactNode {
  return (
    <>
      {hasValue(mainObject.cultura) && (
        <Line label="Cultura:">{mainObject.cultura}</Line>
      )}
      {!!mainObject.talhoes?.length && (
        <Line label="Talhões:">
          {mainObject.talhoes
            .map(
              (g) =>
                `${g.descricao}(${formatNumber(
                  Number(g.areaRecomendada),
                  AREA_FORMAT,
                  LOCALE,
                )} ha)`,
            )
            .join(', ')}
        </Line>
      )}
      {produtos && <div>Produtos: {produtos}</div>}
      {hasValue(mainObject.recomendante) && (
        <Line label="Recomendante:">{mainObject.recomendante}</Line>
      )}
      {(hasValue(mainObject.dataInicial) || hasValue(mainObject.dataFinal)) && (
        <Line label="Previsto:">
          {formatTooltipDate(mainObject.dataInicial)} até{' '}
          {formatTooltipDate(mainObject.dataFinal)}
        </Line>
      )}
      {responsaveis && (
        <Line label="Responsáveis pela aprovação:">{responsaveis}</Line>
      )}
    </>
  );
}

function buildAplicacaoTooltip(
  mainObject: PendingAproveMainObject,
  produtos: React.ReactNode,
  responsaveis: string,
): React.ReactNode {
  return (
    <>
      {hasValue(mainObject.cultura) && (
        <Line label="Cultura:">{mainObject.cultura}</Line>
      )}
      {hasValue(mainObject.estadio) && (
        <Line label="Estádio:">
          {mainObject.estadio!.abreviatura} - {mainObject.estadio!.descricao}
        </Line>
      )}
      {hasValue(mainObject.nrTanques) && (
        <Line label="Tanques aplicados:">
          {formatNumber(
            mainObject.nrTanques!,
            { minimumFractionDigits: 1, maximumFractionDigits: 1 },
            LOCALE,
          )}
        </Line>
      )}
      {hasValue(mainObject.areaAplicada) && (
        <Line label="Área aplicada:">
          {formatNumber(mainObject.areaAplicada!, AREA_FORMAT, LOCALE)} ha
        </Line>
      )}
      {(hasValue(mainObject.dataInicial) || hasValue(mainObject.dataFinal)) && (
        <Line label="Data de aplicação:">
          {formatTooltipDate(mainObject.dataInicial)} -{' '}
          {formatTooltipDate(mainObject.dataFinal)}
        </Line>
      )}
      {produtos && (
        <div>
          <div>Produtos:</div>
          {produtos}
        </div>
      )}
      {hasValue(mainObject.operador) && (
        <Line label="Operador:">{mainObject.operador}</Line>
      )}
      {responsaveis && (
        <Line label="Responsáveis pela aprovação:">{responsaveis}</Line>
      )}
      {hasValue(mainObject.obs) && (
        <Line label="Observações:">{mainObject.obs}</Line>
      )}
    </>
  );
}

const PendingAproveTooltipService = {
  buildTooltipContent(
    mainObject: PendingAproveMainObject,
    item: TypedAplication,
    parsedObjeto: AuditoriaAplicacaoObjeto | null,
    usuariosPorPropriedade: Record<number, Usuario[]>,
  ): React.ReactNode {
    const produtos = buildProdutosConcat(item, parsedObjeto);
    const responsaveis = getResponsaveisAprovacao(
      item.propriedade.id,
      usuariosPorPropriedade,
    );
    if (mainObject.isRecomendacao) {
      return buildRecomendacaoTooltip(mainObject, produtos, responsaveis);
    }
    return buildAplicacaoTooltip(mainObject, produtos, responsaveis);
  },
};

export default PendingAproveTooltipService;
